import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pumpAssignmentsApi } from '@/api/pumpAssignments';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type {
  PumpAssignmentCreateRequest,
  PumpAssignmentUpdateRequest,
} from '@/types/pumpAssignment';

export function usePumpAssignments(deviceId: string | undefined) {
  return useQuery({
    queryKey: ['pump-assignments', deviceId],
    queryFn: () => pumpAssignmentsApi.list(deviceId!),
    enabled: !!deviceId,
    staleTime: CACHE_TIMES.DATA,
  });
}

export function useCreatePumpAssignment(deviceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PumpAssignmentCreateRequest) =>
      pumpAssignmentsApi.create(deviceId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pump-assignments', deviceId] });
      // Also invalidate dosing queries since chemical labels may change
      queryClient.invalidateQueries({ queryKey: ['dosing-events'] });
      queryClient.invalidateQueries({ queryKey: ['analysis'] });
    },
  });
}

export function useUpdatePumpAssignment(deviceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      assignmentId,
      data,
    }: {
      assignmentId: number;
      data: PumpAssignmentUpdateRequest;
    }) => pumpAssignmentsApi.update(deviceId!, assignmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pump-assignments', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events'] });
      queryClient.invalidateQueries({ queryKey: ['analysis'] });
    },
  });
}

export function useDeletePumpAssignment(deviceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: number) =>
      pumpAssignmentsApi.delete(deviceId!, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pump-assignments', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events'] });
      queryClient.invalidateQueries({ queryKey: ['analysis'] });
    },
  });
}
